/**
 * Regras do formulário de equipamento de incêndio (#363): valores em texto como o campo os tem,
 * validação com as MESMAS mensagens do servidor (`FireEquipmentValidator`), o corpo que cada tipo
 * envia e o que "Salvar e cadastrar outro" guarda para o próximo.
 */
import type {
  FireCapacityUnit,
  FireEquipment,
  FireEquipmentCreateData,
  FireEquipmentFieldName,
  FireEquipmentStatus,
  FireEquipmentType,
  FireEquipmentTypeDescriptor,
  FireEquipmentUpdateData,
  FireExtinguishingAgent,
  FireInspectionFrequency,
} from '../../types/fireEquipment';

export interface FireFormValues {
  companyId: string;
  departmentId: string;
  type: FireEquipmentType | '';
  identifier: string;
  location: string;
  extinguishingAgent: FireExtinguishingAgent | '';
  capacityValue: string;
  capacityUnit: FireCapacityUnit | '';
  manufacturer: string;
  manufactureYear: string;
  description: string;
  chargeExpiryDate: string;
  hydrostaticExpiryDate: string;
  inspectionFrequency: FireInspectionFrequency | '';
  status: FireEquipmentStatus;
}

export type FireFormField = keyof FireFormValues;
export type FireFormErrors = Partial<Record<FireFormField, string>>;

export const EMPTY_VALUES: FireFormValues = {
  companyId: '',
  departmentId: '',
  type: '',
  identifier: '',
  location: '',
  extinguishingAgent: '',
  capacityValue: '',
  capacityUnit: '',
  manufacturer: '',
  manufactureYear: '',
  description: '',
  chargeExpiryDate: '',
  hydrostaticExpiryDate: '',
  inspectionFrequency: '',
  status: 'IN_USE',
};

export interface ValidationOptions {
  /** A tela pergunta a empresa (quem vê várias): sem ela o servidor recusa. */
  requireCompany: boolean;
}

const MIN_YEAR = 1900;
const MIN_DATE = '1900-01-01';
const MAX_DATE = '2100-12-31';
const MAX_CAPACITY = 9999.99;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** O nome de cada campo nas mensagens do servidor (`ROTULO_DO_CAMPO`, `requiredText`). */
const SERVER_LABELS: Record<FireFormField, string> = {
  companyId: 'Empresa',
  departmentId: 'Setor',
  type: 'Tipo',
  identifier: 'Identificação',
  location: 'Local',
  extinguishingAgent: 'Agente extintor',
  capacityValue: 'Capacidade',
  capacityUnit: 'Unidade da capacidade',
  manufacturer: 'Fabricante',
  manufactureYear: 'Ano de fabricação',
  description: 'Descrição',
  chargeExpiryDate: 'Validade da carga',
  hydrostaticExpiryDate: 'Validade do teste hidrostático',
  inspectionFrequency: 'Frequência de inspeção',
  status: 'Situação',
};

const MAX_LENGTH: Partial<Record<FireFormField, number>> = {
  identifier: 60,
  location: 255,
  manufacturer: 100,
  description: 5000,
};

/** Os campos que dependem do tipo; os outros (identificação, local, setor) valem para todos. */
const TYPE_FIELDS: readonly FireEquipmentFieldName[] = [
  'extinguishingAgent',
  'capacityValue',
  'capacityUnit',
  'manufacturer',
  'manufactureYear',
  'description',
  'chargeExpiryDate',
  'hydrostaticExpiryDate',
];

function isTypeField(field: FireFormField): field is FireEquipmentFieldName {
  return (TYPE_FIELDS as readonly string[]).includes(field);
}

/** Capacidade digitada com vírgula ou ponto vira número; texto que não é número vira NaN. */
function parseCapacity(raw: string): number {
  return Number(raw.trim().replace(',', '.'));
}

function validDate(raw: string): boolean {
  if (!ISO_DATE.test(raw)) return false;
  const parsed = new Date(`${raw}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== raw) return false;
  return raw >= MIN_DATE && raw <= MAX_DATE;
}

function validateFilled(field: FireFormField, raw: string): string | null {
  const label = SERVER_LABELS[field];
  const limit = MAX_LENGTH[field];
  if (limit !== undefined && raw.trim().length > limit) return `${label} deve ter no máximo ${limit} caracteres`;

  if (field === 'manufactureYear') {
    const year = Number(raw);
    const maxYear = new Date().getFullYear() + 1;
    if (!Number.isInteger(year) || year < MIN_YEAR || year > maxYear) {
      return `${label} deve ser um ano entre ${MIN_YEAR} e ${maxYear}`;
    }
  }

  if (field === 'capacityValue') {
    const capacity = parseCapacity(raw);
    if (!Number.isFinite(capacity) || capacity <= 0 || capacity > MAX_CAPACITY) {
      return `${label} deve ser um número maior que zero e até ${MAX_CAPACITY}`;
    }
    if (Math.abs(capacity * 100 - Math.round(capacity * 100)) > 1e-6) {
      return `${label} aceita no máximo duas casas decimais`;
    }
  }

  if ((field === 'chargeExpiryDate' || field === 'hydrostaticExpiryDate') && !validDate(raw)) {
    return `${label} deve ser uma data válida (AAAA-MM-DD)`;
  }

  return null;
}

/** A mensagem do servidor para o campo, ou `null`. Campo que o tipo não usa nunca tem erro. */
export function validateField(
  field: FireFormField,
  values: FireFormValues,
  descriptor: FireEquipmentTypeDescriptor | undefined,
  options: ValidationOptions
): string | null {
  const raw = String(values[field]);
  const filled = raw.trim() !== '';
  const label = SERVER_LABELS[field];

  if (field === 'companyId') return options.requireCompany && !filled ? 'Informe a empresa do equipamento' : null;
  if (field === 'type') return filled ? null : 'Tipo é obrigatório';
  if (field === 'identifier' || field === 'location') {
    return filled ? validateFilled(field, raw) : `${label} é obrigatório`;
  }
  if (!isTypeField(field)) return null;

  const rule = descriptor?.fields.find((candidate) => candidate.name === field);
  if (!rule) return null;
  if (!filled) return rule.required ? `${label} é obrigatório` : null;
  return validateFilled(field, raw);
}

const ALL_FIELDS = Object.keys(EMPTY_VALUES) as FireFormField[];

export function validateForm(
  values: FireFormValues,
  descriptor: FireEquipmentTypeDescriptor | undefined,
  options: ValidationOptions
): FireFormErrors {
  return ALL_FIELDS.reduce<FireFormErrors>((errors, field) => {
    const message = validateField(field, values, descriptor, options);
    return message ? { ...errors, [field]: message } : errors;
  }, {});
}

export interface PayloadOptions {
  /** Cadastro de quem vê várias empresas: o corpo leva a empresa escolhida. */
  includeCompany: boolean;
  /** Edição de equipamento que continua fora de uso: frequência, validades e status não vão. */
  frozenSchedule?: boolean;
}

const SCHEDULE_FIELDS: readonly FireEquipmentFieldName[] = ['chargeExpiryDate', 'hydrostaticExpiryDate'];

/** O valor de um campo do tipo, já na forma que o servidor espera. */
function fieldValue(field: FireEquipmentFieldName, values: FireFormValues): string | number | null {
  const raw = String(values[field]).trim();
  if (raw === '') return null;
  if (field === 'capacityValue') return parseCapacity(raw);
  if (field === 'manufactureYear') return Number(raw);
  return raw;
}

export function buildPayload(
  values: FireFormValues,
  descriptor: FireEquipmentTypeDescriptor,
  mode: 'create',
  options: PayloadOptions
): FireEquipmentCreateData;
export function buildPayload(
  values: FireFormValues,
  descriptor: FireEquipmentTypeDescriptor,
  mode: 'update',
  options: PayloadOptions
): FireEquipmentUpdateData;
export function buildPayload(
  values: FireFormValues,
  descriptor: FireEquipmentTypeDescriptor,
  mode: 'create' | 'update',
  options: PayloadOptions
): FireEquipmentCreateData | FireEquipmentUpdateData {
  const creating = mode === 'create';

  const typeFields: Partial<Record<FireEquipmentFieldName, string | number | null>> = {};
  descriptor.fields.forEach(({ name }) => {
    if (options.frozenSchedule && SCHEDULE_FIELDS.includes(name)) return;
    const value = fieldValue(name, values);
    if (value !== null || !creating) typeFields[name] = value;
  });

  // Setor vazio: o cadastro omite, a edição o limpa.
  const department = values.departmentId !== '' ? Number(values.departmentId) : creating ? undefined : null;

  const body: FireEquipmentUpdateData = {
    identifier: values.identifier.trim(),
    location: values.location.trim(),
    ...(department !== undefined ? { departmentId: department } : {}),
    // Os campos do tipo foram montados pelo nome que o descritor trouxe; o valor já está na forma do contrato.
    ...(typeFields as Pick<FireEquipmentUpdateData, FireEquipmentFieldName>),
    ...(options.frozenSchedule
      ? {}
      : {
          ...(values.inspectionFrequency !== '' ? { inspectionFrequency: values.inspectionFrequency } : {}),
          status: values.status,
        }),
  };

  if (!creating) return body;
  return { ...body, type: descriptor.type, ...(options.includeCompany ? { companyId: Number(values.companyId) } : {}) };
}

/** O que sobra no formulário depois de "Salvar e cadastrar outro" (spec §7, linha 2). */
export function valuesForNext(values: FireFormValues): FireFormValues {
  // O status não está na lista da spec: o próximo equipamento nasce em uso, mesmo que este tenha sido salvo fora de uso.
  return { ...values, identifier: '', chargeExpiryDate: '', hydrostaticExpiryDate: '', status: 'IN_USE' };
}

export function valuesFromEquipment(equipment: FireEquipment): FireFormValues {
  const text = (value: string | number | null): string => (value === null ? '' : String(value));
  return {
    companyId: String(equipment.companyId),
    departmentId: text(equipment.departmentId),
    type: equipment.type,
    identifier: equipment.identifier,
    location: equipment.location,
    extinguishingAgent: equipment.extinguishingAgent ?? '',
    capacityValue: text(equipment.capacityValue),
    capacityUnit: equipment.capacityUnit ?? '',
    manufacturer: text(equipment.manufacturer),
    manufactureYear: text(equipment.manufactureYear),
    description: text(equipment.description),
    chargeExpiryDate: text(equipment.chargeExpiryDate),
    hydrostaticExpiryDate: text(equipment.hydrostaticExpiryDate),
    inspectionFrequency: equipment.inspectionFrequency,
    status: equipment.status,
  };
}
